/**
 * Device identity and connectivity. Both plugins have a web implementation,
 * so these call the plugin on every platform.
 */
import { toUnsubscribe, type Unsubscribe } from "./platform";

export interface DeviceSummary {
  readonly isVirtual: boolean;
  readonly manufacturer: string;
  readonly model: string;
  readonly osVersion: string;
  readonly platform: "android" | "ios" | "web";
  /** Chrome's version on Android; the browser's on the web. */
  readonly webViewVersion: string;
}

export async function getDeviceInfo(): Promise<DeviceSummary> {
  const { Device } = await import("@capacitor/device");
  const info = await Device.getInfo();
  return {
    isVirtual: info.isVirtual,
    manufacturer: info.manufacturer,
    model: info.model,
    osVersion: info.osVersion,
    platform: info.platform,
    webViewVersion: info.webViewVersion,
  };
}

export type ConnectionType = "cellular" | "none" | "unknown" | "wifi";

export interface NetworkState {
  readonly connected: boolean;
  /** Web: `unknown` whenever connected — a browser does not say which. */
  readonly type: ConnectionType;
}

export async function getNetworkState(): Promise<NetworkState> {
  const { Network } = await import("@capacitor/network");
  const status = await Network.getStatus();
  return { connected: status.connected, type: status.connectionType };
}

export function onNetworkChange(
  handler: (state: NetworkState) => void
): Unsubscribe {
  return toUnsubscribe(
    import("@capacitor/network").then(({ Network }) =>
      Network.addListener("networkStatusChange", (status) =>
        handler({ connected: status.connected, type: status.connectionType })
      )
    )
  );
}
